import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toastError, toastSuccess } from "../utils";
import { IMAGE_URL } from "../constants";
import {
  getAllBusiness,
  getParties,
  getPartyDetails,
  getUserById,
  getUserPlan,
  switchBusiness,
} from "../services/services";
import PaymentModal from "./Modal/payment";
import TransactionModal from "./Modal/Transaction";
import { useDispatch, useSelector } from "react-redux";
import { fetchUser, logout } from "../redux/auth/authSlice";
import { toast } from "react-toastify";
import { confirmLogout } from "../components/confirmDelete";
import ZoomControls from "./zoomControls";
import {
  fetchBusiness,
  setActiveBusiness,
} from "../redux/business/businessSlice";
import { BarChart3, Lock, Pencil, RotateCcw, TrendingUp } from "lucide-react";
import "./header.css";

import {
  LayoutDashboard,
  Box,
  Users,
  ShoppingCart,
  IndianRupee,
  ArrowLeftRight,
  LogOut,
  UserCircle,
  Briefcase,
} from "lucide-react";

const Header = () => {
  const [isBodyClassAdded, setIsBodyClassAdded] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);

  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const sidebarRef = useRef(null);

  const { user } = useSelector((state) => state.auth);
  const { name, _id } = user || {};

  const { freshUser, user: authUser } = useSelector((state) => state.auth);

  const { list: businesses, activeBusiness } = useSelector(
    (state) => state.business,
  );

  const handleNavigate = async () => {
    try {
      const res = await getUserPlan();

      if (!res?.data?.status) {
        toast.error("Failed to fetch plan details");
        return;
      }

      const data = res.data.response || {};
      console.log("Plan Data ", res);

      const businessCount = data.businesses?.length || 0;

      const plan = data.plan;

      if (!plan?.planId?.features) {
        toast.error(
          "You have reached the maximum number of businesses. Please upgrade your plan.",
        );
        return;
      }

      const planDetails = plan.planId;

      const businessFeature = planDetails.features.find(
        (f) => f.name === "Multiple Business",
      );

      if (!businessFeature?.value) {
        toast.error(
          "Multiple business feature is not available in your current plan.",
        );
        return;
      }

      const limit = Number(businessFeature.value);

      if (businessCount >= limit) {
        toast.error(
          `You have reached the maximum limit of ${limit} businesses. Please upgrade your plan.`,
        );
        return;
      }

      navigate("/add-business");
    } catch (error) {
      console.error("Error fetching user plan:", error);
      toast.error("Something went wrong while checking your plan.");
    }
  };

  // const planFeatures = user?.plan?.planId?.features?.map((f) => f.name) || [];

  useEffect(() => {
    if (location.pathname.includes("/sales")) setOpenMenu("sales");
    else if (location.pathname.includes("/purchases")) setOpenMenu("purchases");
    else setOpenMenu(null);
  }, [location.pathname]);

  const toggleMenu = (menu) => {
    setOpenMenu(openMenu === menu ? null : menu);
  };

  const handleButtonClick = () => {
    if (isBodyClassAdded) {
      document.body.classList.remove("toggle-sidebar");
    } else {
      document.body.classList.add("toggle-sidebar");
    }
    setIsBodyClassAdded(!isBodyClassAdded);
  };

  useEffect(() => {
    if (user?._id && !freshUser) {
      dispatch(fetchUser(user._id));
    }
  }, [user?._id, freshUser, dispatch]);

  const handlePaymentSuccess = () => {
    setShowPaymentModal(false);
  };

  const handleLogout = async (_id, title) => {
    const confirmation = await confirmLogout(title);
    if (confirmation) {
      // clear Redux state and token
      dispatch(logout());
      localStorage.clear();

      toastSuccess("Logged out successfully");
      navigate("/login", { replace: true });
    }
  };

  const closeSidebarOnMobile = () => {
    if (window.innerWidth < 992) {
      document.body.classList.remove("toggle-sidebar");
      setIsBodyClassAdded(false);
    }
  };

  // Handle opening the payment modal
  const handleTakePayment = () => {
    setShowPaymentModal(true);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".nav-item.dropdown")) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  useEffect(() => {
    if (user?._id) {
      if (businesses?.length === 0) {
        dispatch(fetchBusiness(user._id));
      }
    }
  }, [user?._id, businesses?.length, dispatch]);

  useEffect(() => {
    if (user?._id && businesses?.length === 0 && !activeBusiness) {
      const stored = JSON.parse(
        localStorage.getItem("selectedBusiness") || "null",
      );
      if (stored && stored._id) {
        dispatch(setActiveBusiness(stored));
      }
      console.log(businesses);
    }
  }, [user?._id, businesses?.length, activeBusiness, dispatch]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      const sidebar = sidebarRef.current;
      const isSidebarOpen = document.body.classList.contains("toggle-sidebar");

      if (
        isSidebarOpen &&
        sidebar &&
        !sidebar.contains(e.target) &&
        !e.target.closest(".toggle-sidebar-btn")
      ) {
        document.body.classList.remove("toggle-sidebar");
        setIsBodyClassAdded(false);
      }
    };

    document.addEventListener("click", handleOutsideClick);

    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  const handleBusinessSelect = async (business) => {
    try {
      if (business._id === activeBusiness?._id) {
        setShowDropdown(false);
        return;
      }

      if (!user?._id) return;

      const res = await switchBusiness({
        userId: user._id,
        businessId: business._id,
      });

      if (!res?.status) {
        toast.error(res?.message || "Failed to switch business");
        return;
      }
      Object.keys(localStorage).forEach((key) => {
        if (
          key.startsWith("pendingSale_") ||
          key.startsWith("pendingPurchase_")
        ) {
          localStorage.removeItem(key);
        }
      });
      dispatch(setActiveBusiness(business));

      toast.success(`Switched to ${business.businessName}`);
      setShowDropdown(false);

      navigate("/dashboard", { replace: true });
    } catch (err) {
      console.error(err);
      toast.error("Error switching business");
    }
  };

  const sidebarNavItems = [
    {
      type: "single",
      to: "/dashboard",
      icon: LayoutDashboard,
      label: "Dashboard",
    },
    {
      type: "dropdown",
      label: "Items & Products",
      icon: Box,
      id: "itemsDropdown",
      featureKey: "Items & Parties",

      subItems: [
        { to: "/items", label: "Product" },
        { to: "/items/category", label: "Categories" },
        { to: "/items/unit", label: "Units" },
        { to: "/items/location", label: "Locations" },
      ],
    },
    {
      type: "dropdown",
      label: "Services",
      icon: Briefcase,
      id: "servicesDropdown",
      subItems: [
        { to: "/services", label: "All Services" },
        { to: "/services/categories", label: "Categories" },
      ],
    },
    {
      type: "single",
      to: "/party",
      icon: Users,
      label: "Parties",
      featureKey: "Items & Parties",
    },
    {
      type: "dropdown",
      label: "Sales",
      icon: TrendingUp,
      id: "sales-nav",
      featureKey: "Sales & Purchases",
      subItems: [
        { to: "/sales/create", label: "Create Sale" },
        { to: "/sales", label: "Manage Sales" },
        { to: "/sales/order", label: "Create Sale Order" },
        { to: "/sales/order/list", label: "Manage Sales Order" },
      ],
    },
    {
      type: "dropdown",
      label: "Purchases",
      icon: ShoppingCart,
      id: "purchase-nav",
      featureKey: "Sales & Purchases",
      subItems: [
        { to: "/purchases/create", label: "Create Purchase" },
        { to: "/purchases", label: "Purchase List" },
        { to: "/purchases/order", label: "Create Purchase Order" },
        { to: "/purchases/order/list", label: "Purchase Order List" },
      ],
    },
    {
      type: "dropdown",
      label: "Expenses",
      icon: IndianRupee,
      id: "expensesDropdown",
      featureKey: "Inventory Management",
      subItems: [
        { to: "/expenses", label: "All Expenses" },
        { to: "/expenses/categories", label: "Categories" },
      ],
    },
    {
      type: "single",
      to: "/returns",
      icon: RotateCcw,
      label: "Return",
      featureKey: "Profit & Loss",
    },
    {
      type: "dropdown",
      label: "Employee",
      icon: UserCircle,
      id: "employeeDropdown",
      featureKey: "Role Permissions",
      subItems: [
        { to: "/employee/shift", label: "Shift" },
        { to: "/employee", label: "Manage Employees" },
        { to: "/employee/attendance", label: "Attendance" },
      ],
    },
    {
      type: "single",
      to: "/transaction",
      icon: ArrowLeftRight,
      label: "Transactions",
    },
    {
      type: "single",
      to: "/reports",
      icon: BarChart3,
      label: "Reports",
    },
    {
      type: "single",
      to: "/prefix",
      icon: Pencil,
      label: "Prefix",
    },
  ];

  return (
    <main>
      <header
        id="header"
        className="header fixed-top d-flex align-items-center bg-white shadow-sm px-3 py-2"
      >
        <div className="d-flex align-items-center justify-content-start flex-grow-1 position-relative">
          <Link
            to="/dashboard"
            className="logo d-none d-md-flex align-items-center gap-2"
          >
            <img
              src="/assets/img/ab_book_logo.png"
              alt="noImage"
              className="img-fluid"
              style={{ height: "3rem", width: "auto" }}
            />
          </Link>

          <Link
            to="/dashboard"
            className="logo d-flex d-md-none align-items-center gap-2"
          >
            <img
              src="/assets/img/ab_book_logo_icon.png"
              alt="noImage"
              className="img-fluid"
              style={{ height: "40px", width: "auto" }}
            />
          </Link>

          {/* Sidebar Toggle (Visible on Mobile/Tablet only) */}
          <i
            className="bi bi-list toggle-sidebar-btn fs-3 text-dark ms-2 d-none d-md-block"
            onClick={handleButtonClick}
            role="button"
          ></i>
        </div>

        <nav className="header-nav ms-auto">
          <ul className="d-flex align-items-center gap-md-3 flex-wrap justify-content-end m-0 p-0 list-unstyled">
            {/* Add ZoomControls here */}
            {/* <li className="nav-item d-none d-md-flex align-items-center">
              <ZoomControls />
            </li> */}

            <li className="nav-item dropdown position-relative">
              <button
                className="nav-link nav-profile d-flex align-items-center bg-transparent border px-2 py-2 rounded"
                onClick={() => setShowDropdown((prev) => !prev)}
              >
                <img
                  src={
                    activeBusiness?.logo
                      ? `${IMAGE_URL}/uploads/business/${activeBusiness.logo}`
                      : "/assets/img/comp_logo.png"
                  }
                  alt="Business Logo"
                  className="rounded-circle me-2"
                  style={{ width: "22px", height: "22px", objectFit: "cover" }}
                  onError={(e) => {
                    e.target.src = "/assets/img/comp_logo.png";
                  }}
                />
                {/* <span
                  className="dropdown-toggle small text-truncate"
                  style={{ maxWidth: "120px" }}
                >
                  {activeBusiness?.businessName || "Select Business"}
                </span> */}

                <span
                  className="dropdown-toggle small"
                  style={{
                    maxWidth: "120px",
                    display: "inline-block",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                  }}
                >
                  {activeBusiness?.businessName || "Select Business"}
                </span>
              </button>

              {showDropdown && (
                <ul
                  className="dropdown-menu business-switch-dropdown "
                  style={{
                    display: "block",
                    position: "absolute",
                    // right: 0,
                    top: "110%",
                    padding: "0",
                    minWidth: "250px",
                    maxWidth: "260px",
                    borderRadius: "8px",
                    zIndex: 1050,
                  }}
                >
                  {/* <li className="px-3 py-2">
                    <h6 className="text-muted small text-uppercase fw-bold mb-0" style={{ fontSize: "10px", letterSpacing: "0.5px" }}>
                      Switch Business
                    </h6>
                  </li> */}

                  <div style={{ maxHeight: "250px", overflowY: "auto" }}>
                    {businesses && businesses.length > 0 ? (
                      businesses.map((biz) => (
                        <li key={biz._id}>
                          <button
                            onClick={() => handleBusinessSelect(biz)}
                            className={`dropdown-item d-flex align-items-center gap-2 py-2 ${
                              activeBusiness?._id === biz._id ? "bg-light" : ""
                            }`}
                          >
                            <div className="position-relative flex-shrink-0">
                              <img
                                src={
                                  biz.logo
                                    ? `${IMAGE_URL}/uploads/business/${biz.logo}`
                                    : "/assets/img/comp_logo.png"
                                }
                                alt="Logo"
                                className="rounded-circle border"
                                style={{
                                  width: "28px",
                                  height: "28px",
                                  objectFit: "cover",
                                }}
                                // onError={(e) => {
                                //   e.target.src = "/assets/img/comp_logo.png";
                                // }}
                              />
                              {activeBusiness?._id === biz._id && (
                                <span
                                  className="position-absolute bottom-0 end-0 p-1 bg-success border border-light rounded-circle"
                                  style={{ width: "8px", height: "8px" }}
                                ></span>
                              )}
                            </div>

                            <span
                              className="overflow-x-auto flex-grow-1"
                              // style={{
                              //   fontSize: "14px",
                              //   fontWeight:
                              //     activeBusiness?._id === biz._id
                              //       ? "600"
                              //       : "400",
                              //   color:
                              //     activeBusiness?._id === biz._id
                              //       ? "#3f51b5"
                              //       : "#444",
                              // }}
                              title={biz.businessName}
                            >
                              {biz.businessName}
                            </span>
                          </button>
                        </li>
                      ))
                    ) : (
                      <li className="px-3 py-2 text-center">
                        <p className="text-muted small mb-0">
                          No businesses found
                        </p>
                      </li>
                    )}
                  </div>

                  <li>
                    <hr className="dropdown-divider opacity-50" />
                  </li>

                  <li>
                    <button
                      className="dropdown-item d-flex align-items-center text-primary fw-medium py-2"
                      onClick={() => {
                        setShowDropdown(false);
                        handleNavigate();
                      }}
                    >
                      <i className="bi bi-plus-circle-fill me-2"></i>
                      <span style={{ fontSize: "14px" }}>Add New Business</span>
                    </button>
                  </li>
                </ul>
              )}
            </li>

            <i
              className="bi bi-list toggle-sidebar-btn fs-3 text-dark ms-2 d-block d-md-none"
              onClick={handleButtonClick}
              role="button"
            ></i>

            {/* Buttons - Hidden on small screens */}
            <div className="d-none d-md-flex align-items-center gap-2">
              <li>
                <button
                  className="btn btn-primary btn-sm d-flex align-items-center justify-content-center"
                  onClick={handleTakePayment}
                >
                  Payments
                </button>
              </li>
              <li>
                <button
                  className="btn btn-custom btn-sm d-flex align-items-center justify-content-center"
                  onClick={() => navigate("/sales/create")}
                >
                  Create Sales
                </button>
              </li>
              <li>
                <button
                  className="btn rounded-circle d-flex align-items-center justify-content-center"
                  style={{
                    width: "36px",
                    height: "36px",
                    fontSize: "22px",
                    background: "#0b2b6d",
                    color: "#fff",
                  }}
                  onClick={() => setShowTransactionModal(true)}
                >
                  +
                </button>
              </li>
            </div>
          </ul>
        </nav>
      </header>

      <aside
        id="sidebar"
        ref={sidebarRef}
        className={`sidebar d-flex flex-column justify-between`}
        style={{ color: "#0b2b6d" }}
      >
        {/* Sidebar Nav */}
        <ul className="sidebar-nav flex-grow-1" id="sidebar-nav">
          {sidebarNavItems.map((item) => {
            const isActive =
              item.type === "single"
                ? location.pathname.includes(item.to)
                : item.subItems?.some((sub) =>
                    location.pathname.includes(sub.to),
                  );

            const isCollapsed = item.type === "dropdown" && !isActive;

            // LOCK LOGIC
            // const isLocked =
            //   item.featureKey && !planFeatures.includes(item.featureKey);

            // SINGLE ITEM
            // if (item.type === "single") {
            //   return (
            //     <li className="nav-item" key={item.to}>
            //       <Link
            //         className={`nav-link ${isActive ? "" : "collapsed"} ${isLocked ? "pe-none opacity-50" : ""
            //           }`}
            //         to={isLocked ? "#" : item.to}
            //         onClick={() => {
            //           closeSidebarOnMobile();
            //           if (isLocked) {
            //             toast.error("Upgrade your plan to access this feature");
            //           }
            //         }}
            //       >
            //         <item.icon
            //           className="me-2"
            //           style={{
            //             fontSize: "12px",
            //             height: "1rem",
            //             width: "1rem",
            //           }}
            //         />
            //         <span>{item.label}</span>

            //         {isLocked && (
            //           <Lock size={16} className="ms-2 text-warning" />
            //         )}
            //       </Link>
            //     </li>
            //   );
            // }

            // // DROPDOWN
            // return (
            //   <li className="nav-item" key={item.id}>
            //     <button
            //       className={`w-100 nav-link ${isCollapsed ? "collapsed" : ""} ${isLocked ? "opacity-50" : ""
            //         }`}
            //       onClick={(e) => {
            //         if (isLocked) {
            //           e.preventDefault();
            //           toast.error("Upgrade your plan to access this feature");
            //         }
            //       }}
            //       data-bs-toggle="collapse"
            //       data-bs-target={`#${item.id}`}
            //     >
            //       <item.icon
            //         className="me-2"
            //         style={{
            //           fontSize: "12px",
            //           height: "1rem",
            //           width: "1rem",
            //         }}
            //       />
            //       <span>{item.label}</span>

            //       {isLocked ? (
            //         <Lock size={16} className="ms-auto text-warning" />
            //       ) : (
            //         <i
            //           className="bi bi-chevron-down ms-2"
            //           style={{
            //             fontSize: "13px",
            //             height: "1rem",
            //             width: "1rem",
            //           }}
            //         ></i>
            //       )}
            //     </button>

            //     {!isLocked && (
            //       <ul
            //         id={item.id}
            //         className={`nav-content collapse ${isActive ? "show" : ""}`}
            //       >
            //         {item.subItems.map((sub) => (
            //           <li
            //             key={sub.to}
            //             style={{ fontSize: "13px", fontWeight: "600" }}
            //           >
            //             <Link to={sub.to} onClick={closeSidebarOnMobile}>
            //               <i
            //                 className={`bi ${location.pathname === sub.to ? "bi-circle-fill" : "bi-circle"}`}
            //               ></i>
            //               <span>{sub.label}</span>
            //             </Link>
            //           </li>
            //         ))}
            //       </ul>
            //     )}
            //   </li>
            // );

            if (item.type === "single") {
              return (
                <li className="nav-item" key={item.to}>
                  <Link
                    className={`nav-link ${isActive ? "" : "collapsed"}`}
                    to={item.to}
                    onClick={closeSidebarOnMobile}
                  >
                    <item.icon
                      className="me-2"
                      style={{
                        fontSize: "12px",
                        height: "1rem",
                        width: "1rem",
                      }}
                    />
                    <span>{item.label}</span>
                  </Link>
                </li>
              );
            }

            // DROPDOWN
            return (
              <li className="nav-item" key={item.id}>
                <button
                  className={`w-100 nav-link ${isCollapsed ? "collapsed" : ""}`}
                  data-bs-toggle="collapse"
                  data-bs-target={`#${item.id}`}
                >
                  <item.icon
                    className="me-2"
                    style={{ fontSize: "12px", height: "1rem", width: "1rem" }}
                  />
                  <span>{item.label}</span>
                  <i
                    className="bi bi-chevron-down ms-2"
                    style={{ fontSize: "13px", height: "1rem", width: "1rem" }}
                  ></i>
                </button>

                <ul
                  id={item.id}
                  className={`nav-content collapse ${isActive ? "show" : ""}`}
                >
                  {item.subItems.map((sub) => (
                    <li
                      key={sub.to}
                      style={{ fontSize: "13px", fontWeight: "600" }}
                    >
                      <Link to={sub.to} onClick={closeSidebarOnMobile}>
                        <i
                          className={`bi ${
                            location.pathname === sub.to
                              ? "bi-circle-fill"
                              : "bi-circle"
                          }`}
                        ></i>
                        <span>{sub.label}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </li>
            );
          })}

          {/* Logout */}

          <li className="nav-item">
            <button
              className="nav-link d-flex align-items-center logout-button"
              onClick={() => handleLogout(user._id)}
              style={{
                background: "transparent",
                color: "#012970",
                cursor: "pointer",
              }}
            >
              <LogOut
                className="me-2"
                style={{
                  color: "#899bbd",
                  fontSize: "12px",
                  height: "1rem",
                  width: "1rem",
                }}
              />
              <span>Sign Out</span>
            </button>
          </li>
        </ul>

        {/* User Dropdown at Bottom */}
        <div className="sidebar-footer mt-auto position-relative border-top">
          <div className="dropdown w-100 pt-3">
            <button
              className="nav-link w-100 d-flex align-items-center justify-content-between"
              data-bs-toggle="dropdown"
              style={{ border: "none", background: "transparent" }}
            >
              <div className="d-flex align-items-center flex-grow-1 text-start">
                {/* Logo */}
                {activeBusiness?.logo ? (
                  <img
                    src={`${IMAGE_URL}/uploads/business/${activeBusiness.logo}`}
                    alt="Logo"
                    className="rounded-circle me-2"
                    style={{
                      width: "26px",
                      height: "26px",
                      objectFit: "cover",
                    }}
                    onError={(e) => {
                      e.target.src = "/assets/img/comp_logo.png";
                    }}
                  />
                ) : (
                  <img
                    src="/assets/img/comp_logo.png"
                    alt="Default Logo"
                    className="rounded-circle me-2"
                    style={{
                      width: "26px",
                      height: "26px",
                      objectFit: "cover",
                    }}
                  />
                )}

                {/* Business Name */}
                <span className="text-truncate" style={{ maxWidth: "120px" }}>
                  {activeBusiness?.businessName || "Select Business"}
                </span>
              </div>

              <i className="bi bi-chevron-up"></i>
            </button>

            {/*  Custom dropdown menu */}
            <ul className="dropdown-menu custom-sidebar-dropdown">
              <li>
                <Link className="dropdown-item" to="/business">
                  Manage Business
                </Link>
              </li>
              <li>
                <Link to="/account" className="dropdown-item">
                  Account
                </Link>
              </li>
              {/* 
              <li>
                <button className="dropdown-item">Plans</button>
              </li> */}
            </ul>
          </div>
        </div>
      </aside>

      {/* Mobile Footer Buttons - Visible only on small screens */}
      <div
        className="d-md-none fixed-bottom bg-white shadow-sm border-top p-3 d-flex justify-content-around align-items-center w-100"
        style={{ zIndex: "99" }}
      >
        <button
          className="btn btn-primary btn-sm flex-grow-1 me-1"
          style={{ minWidth: "80px" }}
          onClick={handleTakePayment}
        >
          Take Payment
        </button>
        <button
          className="btn rounded-circle d-flex align-items-center justify-content-center mx-3"
          style={{
            width: "36px",
            height: "36px",
            fontSize: "22px",
            background: "#0b2b6d",
            color: "#fff",
            flexShrink: 0,
          }}
          onClick={() => setShowTransactionModal(true)}
        >
          +
        </button>
        <button
          className="btn btn-custom btn-sm flex-grow-1 mx-1"
          style={{ minWidth: "80px" }}
          onClick={() => navigate("/sales/create")}
        >
          Create Invoice
        </button>
      </div>

      {showPaymentModal && (
        <PaymentModal
          paymentType="payment-in"
          onClose={() => {
            setShowPaymentModal(false);
          }}
          onPaymentSuccess={handlePaymentSuccess}
        />
      )}
      {showTransactionModal && (
        <TransactionModal
          show={showTransactionModal}
          onHide={() => setShowTransactionModal(false)}
          // party={partyData}
          onPaymentSuccess={handlePaymentSuccess}
        />
      )}
    </main>
  );
};

export default Header;
